import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Trash2, Layers, ChevronLeft, ChevronRight, Save, Pencil, X, Copy } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import { API } from '../utils/api';

const INSTRUMENTS = [
  { value: "stocks", label: "Stocks" },
  { value: "etfs", label: "ETFs" },
  { value: "bonds", label: "Bonds" },
  { value: "mutual_funds", label: "Mutual Funds" },
  { value: "crypto", label: "Cryptocurrency" },
  { value: "real_estate", label: "Property" },
  { value: "commodities", label: "Commodities" },
  { value: "cash", label: "Cash" },
];

const COLORS = ["#22c55e", "#06b6d4", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#a855f7"];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  }).format(value);
};

// Custom tooltip with better contrast
const CustomTooltip = ({ active, payload, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 shadow-lg">
        <p className="text-white font-medium">{payload[0].name}</p>
        <p className="text-green-400 font-bold">
          {formatCurrency(payload[0].value, currency)}
        </p>
      </div>
    );
  }
  return null;
};

// Custom label to show percentage inside pie chart
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      className="text-xs font-bold"
      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function Allocations({ user }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newAllocation, setNewAllocation] = useState({
    instrument: "",
    name: "",
    amount: "",
  });
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editAllocation, setEditAllocation] = useState({
    instrument: "",
    name: "",
    amount: "",
  });

  // Open edit modal with selected allocation
  const handleEditClick = (index) => {
    const alloc = allocations[index];
    setEditingIndex(index);
    setEditAllocation({
      instrument: alloc.instrument,
      name: alloc.name,
      amount: alloc.amount.toString(),
    });
    setEditModalOpen(true);
  };

  // Save edited allocation
  const handleUpdateAllocation = () => {
    if (!editAllocation.instrument || !editAllocation.name || !editAllocation.amount) {
      toast.error("Please fill all fields");
      return;
    }

    const updatedAllocations = [...allocations];
    updatedAllocations[editingIndex] = {
      ...updatedAllocations[editingIndex],
      instrument: editAllocation.instrument,
      name: editAllocation.name,
      amount: parseFloat(editAllocation.amount),
    };

    setAllocations(updatedAllocations);
    setEditModalOpen(false);
    setEditingIndex(null);
    toast.success("Allocation updated - click Save to persist changes");
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => {
    fetchAllocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const fetchAllocations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/allocations/${year}/${month}`);
      setAllocations(response.data.allocations || []);
    } catch (error) {
      console.error("Error fetching allocations:", error);
      setAllocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAllocation = () => {
    if (!newAllocation.instrument || !newAllocation.name || !newAllocation.amount) {
      toast.error("Please fill all fields");
      return;
    }

    const allocation = {
      ...newAllocation,
      amount: parseFloat(newAllocation.amount),
      id: Date.now(),
    };

    setAllocations([...allocations, allocation]);
    setNewAllocation({
      instrument: "",
      name: "",
      amount: "",
    });
  };

  const handleRemoveAllocation = (index) => {
    setAllocations(allocations.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/allocations`, {
        year,
        month,
        allocations: allocations.map(({ id, ...alloc }) => alloc),
      });
      toast.success("Allocations saved");
    } catch (error) {
      console.error("Error saving allocations:", error);
      toast.error("Failed to save allocations");
    } finally {
      setSaving(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Copy allocations from previous month
  const handleCopyFromPreviousMonth = async () => {
    // Calculate previous month
    let prevMonth = month - 1;
    let prevYear = year;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear = year - 1;
    }

    try {
      const response = await axios.get(`${API}/allocations/${prevYear}/${prevMonth}`);
      const prevAllocations = response.data.allocations || [];
      
      if (prevAllocations.length === 0) {
        toast.error(`No allocations found for ${MONTHS[prevMonth - 1]} ${prevYear}`);
        return;
      }

      // Add unique IDs to copied allocations
      const copiedAllocations = prevAllocations.map((alloc, index) => ({
        ...alloc,
        id: Date.now() + index,
      }));

      setAllocations(copiedAllocations);
      toast.success(`Copied ${copiedAllocations.length} allocations from ${MONTHS[prevMonth - 1]} ${prevYear} - click Save to persist`);
    } catch (error) {
      console.error("Error copying allocations:", error);
      toast.error("Failed to copy allocations from previous month");
    }
  };

  const currency = user?.default_currency || "EUR";
  const totalAmount = allocations.reduce((sum, a) => sum + a.amount, 0);

  // Group allocations by instrument for pie chart
  const groupedByInstrument = allocations.reduce((acc, alloc) => {
    const existing = acc.find(a => a.instrument === alloc.instrument);
    if (existing) {
      existing.value += alloc.amount;
    } else {
      acc.push({
        instrument: alloc.instrument,
        name: INSTRUMENTS.find(i => i.value === alloc.instrument)?.label || alloc.instrument,
        value: alloc.amount,
      });
    }
    return acc;
  }, []);

  if (loading) {
    return (
      <Layout user={user} pageTitle="Investment Allocations">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Investment Allocations">
      <div data-testid="allocations-page" className="space-y-8">
        {/* Month Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth(-1)}
            className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
            data-testid="prev-month-alloc-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <h2 className="text-xl font-bold text-white">
              {MONTHS[month - 1]} {year}
            </h2>
            <p className="text-sm text-gray-500">Monthly Investment Allocations</p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateMonth(1)}
            className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
            data-testid="next-month-alloc-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Summary */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <Layers className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Invested This Month</p>
                    <p className="text-3xl font-bold text-green-400 number-display" data-testid="total-allocated">
                      {formatCurrency(totalAmount, currency)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleCopyFromPreviousMonth}
                  className="border-border text-gray-300 hover:text-white hover:bg-secondary"
                  data-testid="copy-prev-month-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy from Previous Month
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Add Allocation Form */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-white">Add Allocation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Instrument Type</Label>
                <Select
                  value={newAllocation.instrument}
                  onValueChange={(value) => setNewAllocation({ ...newAllocation, instrument: value })}
                >
                  <SelectTrigger className="mt-1 bg-secondary border-border text-white" data-testid="instrument-select">
                    <SelectValue placeholder="Select instrument" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {INSTRUMENTS.map((inst) => (
                      <SelectItem key={inst.value} value={inst.value} className="text-gray-300 focus:text-white focus:bg-secondary">
                        {inst.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-300">Name / Description</Label>
                <Input
                  value={newAllocation.name}
                  onChange={(e) => setNewAllocation({ ...newAllocation, name: e.target.value })}
                  placeholder="e.g., MSCI World ETF"
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  data-testid="allocation-name-input"
                />
              </div>

              <div>
                <Label className="text-gray-300">Amount ({currency})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newAllocation.amount}
                  onChange={(e) => setNewAllocation({ ...newAllocation, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  data-testid="allocation-amount-input"
                />
              </div>

              <Button
                onClick={handleAddAllocation}
                className="w-full bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
                data-testid="add-allocation-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Allocation
              </Button>
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-white">Allocation Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {groupedByInstrument.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={groupedByInstrument}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={renderCustomLabel}
                        labelLine={false}
                      >
                        {groupedByInstrument.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip currency={currency} />} />
                      <Legend wrapperStyle={{ color: "#a1a1aa" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  Add allocations to see the breakdown
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Allocations List */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold text-white">This Month's Allocations</CardTitle>
          </CardHeader>
          <CardContent>
            {allocations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No allocations for this month</p>
            ) : (
              <div className="space-y-2">
                {allocations.map((alloc, index) => (
                  <motion.div
                    key={alloc.id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between p-4 bg-secondary/50 border border-border rounded-xl"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: COLORS[INSTRUMENTS.findIndex(i => i.value === alloc.instrument) % COLORS.length],
                        }}
                      />
                      <div>
                        <p className="font-medium text-white">{alloc.name}</p>
                        <p className="text-sm text-gray-400">
                          {INSTRUMENTS.find(i => i.value === alloc.instrument)?.label || alloc.instrument}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-green-400 number-display mr-2">
                        {formatCurrency(alloc.amount, currency)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500 hover:text-blue-400"
                        onClick={() => handleEditClick(index)}
                        data-testid={`edit-allocation-btn-${index}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500 hover:text-red-400"
                        onClick={() => handleRemoveAllocation(index)}
                        data-testid={`delete-allocation-btn-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-500 hover:bg-green-400 text-black rounded-full px-8 font-semibold"
            data-testid="save-allocations-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Allocations"}
          </Button>
        </div>

        {/* Edit Allocation Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="bg-card border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Allocation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-300">Instrument Type</Label>
                <Select
                  value={editAllocation.instrument}
                  onValueChange={(value) => setEditAllocation({ ...editAllocation, instrument: value })}
                >
                  <SelectTrigger className="mt-1 bg-secondary border-border text-white" data-testid="edit-instrument-select">
                    <SelectValue placeholder="Select instrument" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {INSTRUMENTS.map((inst) => (
                      <SelectItem key={inst.value} value={inst.value} className="text-gray-300 focus:text-white focus:bg-secondary">
                        {inst.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-300">Name / Description</Label>
                <Input
                  value={editAllocation.name}
                  onChange={(e) => setEditAllocation({ ...editAllocation, name: e.target.value })}
                  placeholder="e.g., MSCI World ETF"
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  data-testid="edit-allocation-name-input"
                />
              </div>

              <div>
                <Label className="text-gray-300">Amount ({currency})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editAllocation.amount}
                  onChange={(e) => setEditAllocation({ ...editAllocation, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  data-testid="edit-allocation-amount-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEditModalOpen(false)}
                  className="flex-1 border-border text-gray-300 hover:bg-secondary"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateAllocation}
                  className="flex-1 bg-green-500 hover:bg-green-400 text-black font-semibold"
                  data-testid="update-allocation-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Update
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
